import React, { useEffect, useState } from 'react';
import {
  Text,
  Button,
  Select,
  Grid,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Alert,
  AlertIcon,
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
} from '@chakra-ui/react';
import { apiService } from '../ExternalApiService';
import { useToast } from '@chakra-ui/react';
import keycloakClient from '../Keycloak';
import { extractPlaceholders } from '../util/templateUtil';
import { ImMagicWand } from "react-icons/im";
import { MotionVStack, MotionGrid, MotionCard, MotionBox, variants } from '../components/motion';

const DashboardHome = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvFile, setCsvFile] = useState('');
  const [selectedDataId, setSelectedDataId] = useState('');
  const [currentDataIndex, setCurrentDataIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [previewData, setPreviewData] = useState('');
  const [usages, setUsages] = useState(0);
  const [templatesFetched, setTemplatesFetched] = useState(false);
  const [usagesFetched, setUsagesFetched] = useState(false);
  const [email, setEmail] = useState('');
  const [userFullName, setUserFullName] = useState('');

  const toast = useToast();

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!keycloakClient.authenticated) return;

      apiService.setKeycloakClient(keycloakClient);

      // try {
      //   await keycloakClient.updateToken(30).catch(keycloakClient.login);
      // } catch {
      //   //handle login redirect
      //   return;
      // }

      if (!alive) return;

      const { email, name } = keycloakClient.tokenParsed || {};
      setEmail(email || '');
      setUserFullName(name || '');

      apiService.fetchTemplates()
        .then(t => { if (alive) setTemplates(t); })
        .catch(err => console.error('Failed to fetch templates:', err))
        .finally(() => { if (alive) setTemplatesFetched(true); });

      apiService.getUsageSummary()
        .then(u => { if (alive) setUsages(u); })
        .catch(err => console.error('Failed to fetch usages:', err))
        .finally(() => { if (alive) setUsagesFetched(true); });

      // keycloakClient.onTokenExpired = async () => {
      //   try {
      //     await keycloakClient.updateToken(30);
      //   } catch {
      //     console.log('Session expired');
      //   }
      // };
    };

    run();

    return () => { alive = false; };
  }, []);
  const onClose = () => setIsOpen(false);

  const handleFileChange = event => {
    const file = event.target.files[0];
    setCsvFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        const text = e.target.result;

        if (!doesCSVHavePhoneNumber(text)) {
          showToast(
            'error',
            'CSV does not have a sendTo column.',
            'Please upload a CSV with a sendTo column.'
          );
          return;
        }

        parseCSVtoJSON(text);
      };
      reader.readAsText(file);
    }
  };

  const doesCSVHavePhoneNumber = csv => {
    const lines = csv.split('\n');
    const headers = lines[0]
      .split(',')
      .map(header => header.trim().replace(/\r$/, ''));
    return headers.includes('sendTo');
  };

  const parseCSVtoJSON = csv => {
    const lines = csv.split('\n');
    const result = [];
    const headers = lines[0]
      .split(',')
      .map(header => header.trim().replace(/\r$/, ''));

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const obj = {};
      const currentLine = lines[i]
        .split(',')
        .map(value => value.trim().replace(/\r$/, ''));

      for (let j = 0; j < headers.length; j++) {
        obj[headers[j]] = currentLine[j];
      }
      result.push(obj);
    }

    if (result.length > 0) {
      const firstSendTo = result[0].sendTo;
      const messageType = getMessageType(firstSendTo);
      const invalidEntries = result.filter(row => getMessageType(row.sendTo) !== messageType);

      if (invalidEntries.length > 0) {
        showToast(
          'error',
          'Mixed message types',
          'All sendTo values must be of the same type (either all emails or all phone numbers)'
        );
        return;
      }
    }

    setCsvData(result);
  };

  const showToast = (status, title, description = '') => {
    toast({
      title,
      description,
      status,
      duration: 5000,
      isClosable: true,
      position: 'top',
    });
  };

  const handleTemplateChange = event => {
    const templateId = event.target.value;
    setSelectedTemplateId(templateId);
    if (templateId) {
      apiService
        .fetchTemplateById(templateId)
        .then(template => {
          setTemplateContent(template.content);
          setTemplateName(template.name);
        })
        .catch(error =>
          showToast('error', `Failed to fetch template ${templateId}:`, error)
        );
    } else {
      setTemplateContent('');
      setTemplateName('');
    }
  };

  const handleDataChange = event => {
    const dataId = event.target.value;
    setSelectedDataId(dataId);
    if (dataId) {
      setCurrentDataIndex(dataId);
    }
  };

  const handlePreview = () => {
    if (selectedTemplateId && selectedDataId) {
      setIsSaving(true);
      apiService
        .previewTemplate(selectedTemplateId, csvData[currentDataIndex])
        .then(data => {
          setPreviewData(data);
          setIsOpen(true);
        })
        .catch(error => {
          showToast(
            'error',
            `Failed to preview template ${selectedTemplateId}:`,
            error
          );
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const getMessageType = (sendTo) => {
    if (!sendTo) return null;

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^1876\d{7}$/;

    if (emailPattern.test(sendTo)) return 'email';
    if (phonePattern.test(sendTo)) return 'sms';

    return null;
  };

  const handleSendMessage = (templateId, dataId) => {
    if (templateId && dataId) {
      const messageType = getMessageType(csvData[currentDataIndex]?.sendTo);

      if (!messageType) {
        showToast(
          'error',
          'Invalid sendTo format',
          'The sendTo value must be either a valid email address or phone number (18760000000)'
        );
        return;
      }

      if (messageType === 'email') {
        handleSendEmail(templateId, dataId);
      } else {
        handleSendTextMessage(templateId, dataId);
      }
    }
  };

  const handleSendTextMessage = (templateId, dataId) => {
    if (templateId && dataId) {
      const data = JSON.stringify(csvData[dataId]);

      const messageData = {
        template_id: templateId,
        content_values: data,
        phone_number: csvData[currentDataIndex].sendTo,
        status: 'pending',
      };

      setIsSaving(true);

      apiService
        .createMessage(messageData)
        .then(data => {
          const messageId = data.id;
          apiService
            .sendMessage(messageId)
            .then(() => {
              showToast('success', 'Message sent successfully.');
              apiService
                .getUsageSummary()
                .then(setUsages)
                .catch(error =>
                  console.error('Failed to fetch usage summary:', error)
                );
            })
            .catch(error => {
              showToast(
                'error',
                'Failed to send message:',
                JSON.stringify(error?.message || 'Failed to send message')
              );
            });
        })
        .catch(error => {
          showToast(
            'error',
            'Failed to create message:',
            JSON.stringify(error)
          );
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const handleSendEmail = (templateId, dataId) => {
    if (templateId && dataId) {
      const data = JSON.stringify(csvData[dataId]);
      const emailData = {
        template_id: templateId.toString(),
        content_values: data,
        to: csvData[currentDataIndex].sendTo
      };

      setIsSaving(true);
      apiService.sendEmail(emailData).then(data => {
        showToast('success', 'Email sent successfully.');
        apiService.getUsageSummary().then(setUsages).catch(error => console.error('Failed to fetch usage summary:', error));
      })
        .catch(error => {
          showToast('error', 'Failed to send email:', JSON.stringify(error));
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const handleBulkSendMessages = (templateId, csvFile) => {
    if (!csvData.length) return;

    const messageType = getMessageType(csvData[0]?.sendTo);

    if (!messageType) {
      showToast(
        'error',
        'Invalid sendTo format',
        'The sendTo values must be either valid email addresses or phone numbers (18760000000)'
      );
      return;
    }

    if (messageType === 'email') {
      handleBulkSendEmails(templateId, csvFile);
    } else {
      handleBulkSendTextMessages(templateId, csvFile);
    }
  };

  const handleBulkSendEmails = (templateId, csvFile) => {
    setIsSaving(true);
    apiService
      .sendBulkEmails(templateId, csvFile)
      .then(() => {
        showToast('success', 'Bulk emails successfully queued.');
        apiService
          .getUsageSummary()
          .then(setUsages)
          .catch(error => console.error('Failed to fetch usage summary:', error));
      })
      .catch(error => {
        showToast(
          'error',
          'Failed to queue bulk emails:',
          JSON.stringify(error)
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleBulkSendTextMessages = (templateId, csvFile) => {
    setIsSaving(true);
    apiService
      .sendBulkMessages(templateId, csvFile)
      .then(() => {
        showToast('success', 'Bulk messages successfully queued.');
        apiService
          .getUsageSummary()
          .then(setUsages)
          .catch(error => console.error('Failed to fetch usage summary:', error));
        setIsSaving(false);
      })
      .catch(error => {
        showToast(
          'error',
          'Failed to queue bulk messages:',
          JSON.stringify(error)
        );
        setIsSaving(false);
      });
  };

  const downloadCsv = (filename, headers) => {
    const uniqueHeaders = [...headers.filter(h => h !== 'sendTo'), 'sendTo'];
    const csv = [uniqueHeaders.join(','), '', ''].join('\n');
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    
    a.href = url;
    a.download = `${(templateName || 'template').replace(/\s+/g, '_').toLowerCase()}_templateSheet.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCsvForTemplate = () => {
    if (!templateContent) return;
    const placeholders = extractPlaceholders(templateContent);
    if (placeholders.length === 0) {
      showToast('info', 'No placeholders found', 'This template has no {{placeholders}}.');
      return;
    }
    downloadCsv(templateName || 'template', placeholders);
  };

  return (
    <MotionVStack spacing={6} align="stretch" variants={variants.container} initial="hidden" animate="show">
      <Box>
        <MotionBox variants={variants.item}>
          <Heading size="lg" mb={2}>Dashboard</Heading>
          <HStack spacing={4}>
            <Text fontSize="sm" color="gray.600">
              Welcome back, {userFullName || email}!
            </Text>
          </HStack>
        </MotionBox>
      </Box>

      {(templatesFetched && usagesFetched && templates.length === 0 && ((usages?.remainingCredits || 0) === 0)) && (
        <MotionBox variants={variants.item}>
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <HStack justify="space-between" w="full">
              <Text as="a" href="https://smartsendtest.zohodesk.com/portal/en/kb/articles/sending-your-first-message-on-smartsend" target="_blank" rel="noopener noreferrer" textDecoration="underline">
                Learn how to send your first message
              </Text>
              <Button as="a" href="https://smartsendtest.zohodesk.com/portal/en/kb/articles/sending-your-first-message-on-smartsend" target="_blank" rel="noopener noreferrer" colorScheme="blue" size="sm">
                Open Guide
              </Button>
            </HStack>
          </Alert>
        </MotionBox>
      )}

      <MotionGrid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} variants={variants.container}>
        <MotionCard variants={variants.item}>
          <CardBody>
            <Text fontSize="sm" color="gray.600">Total Templates</Text>
            <Text fontSize="2xl" fontWeight="bold">{templates.length}</Text>
          </CardBody>
        </MotionCard>
        <MotionCard variants={variants.item}>
          <CardBody>
            <Text fontSize="sm" color="gray.600">{`Credit${usages?.remainingCredits > 1 ? 's' : ''}`} Available</Text>
            <Text fontSize="2xl" fontWeight="bold">{usages?.remainingCredits || 0}</Text>
          </CardBody>
        </MotionCard>
      </MotionGrid>

      <MotionCard variants={variants.item}>
        <CardHeader>
          <Heading size="md">Send Messages</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Select
              placeholder="Select template"
              value={selectedTemplateId}
              onChange={handleTemplateChange}
            >
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </Select>
          </VStack>
        </CardBody>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Grid templateColumns="1fr 1fr" gap={4}>
              <Input
                type="file"
                accept=".csv"
                p={1}
                onChange={handleFileChange}
              />
              <Select
                placeholder="Select Data Row"
                value={selectedDataId}
                onChange={handleDataChange}
              >
                {csvData.map((data, index) => {
                  const capitalizeFirstLetter = string =>
                    string.charAt(0).toUpperCase() + string.slice(1);
                  const formattedData = Object.entries(data)
                    .map(
                      ([key, value]) =>
                        `${capitalizeFirstLetter(key)}: ${value}`
                    )
                    .join(', ');
                  return (
                    <option key={index} value={index}>
                      {formattedData}
                    </option>
                  );
                })}
              </Select>
            </Grid>

            <Alert status="info">
              <AlertIcon />
              Please ensure the number is formatted as "18760000000"
            </Alert>

            <Button
              leftIcon={<ImMagicWand />}
              onClick={handleDownloadCsvForTemplate}
              hidden={!selectedTemplateId || !templateContent}
              width="fit-content"
              bg="#92D600"
              _hover={{ bg: '#8ac509' }}
              _active={{ bg: '#7fb306' }}
              color="white"
            >
              Generate Template
            </Button>
            <Button
              w="100%"
              isDisabled={selectedDataId === ''}
              onClick={handlePreview}
            >
              Preview
            </Button>


            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {csvData.length > 0 && (
                <>
                  <Button
                    onClick={() => handleSendMessage(selectedTemplateId, selectedDataId)}
                    isDisabled={selectedTemplateId === '' || selectedDataId === ''}
                  >
                    Send {getMessageType(csvData[currentDataIndex]?.sendTo) === 'email' ? 'Email' : 'Text'} Message
                  </Button>
                  <Button
                    onClick={() => handleBulkSendMessages(selectedTemplateId, csvFile)}
                    isDisabled={selectedTemplateId === '' || !csvFile}
                  >
                    Bulk Send {getMessageType(csvData[0]?.sendTo) === 'email' ? 'Emails' : 'Text Messages'}
                  </Button>
                </>
              )}
            </Grid>

            {csvData.length > 0 && (
              <Alert status="info" >
                <AlertIcon />
                Detected {getMessageType(csvData[0]?.sendTo) === 'email' ? 'email addresses' : 'phone numbers'} in the CSV file
              </Alert>
            )}
          </VStack>
        </CardBody>
      </MotionCard>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{previewData}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MotionVStack>
  );
};

export default DashboardHome;
